/**
 * Merch planner — del evento al motor (puro).
 *
 * Traduce lo que lee la página (estaciones, asignaciones y merch del evento) en los
 * pools del motor, con la regla del juego del servidor (`xotski.data-source.ts:99-171`):
 * por estación `Enable` y merch cuenta UNA ranura, la fila jugable de menor `id`.
 * Tipos estructurales mínimos: XkStation / XkEventStockStation / XkStock encajan tal cual.
 */
import { rarityWeight, slotWeight } from './engine';
import type { PlanPool, PlanResult, PlanSlot } from './engine';

export interface AllocLike {
  id: number;
  stationId: number;
  stockId: number;
  status?: string;
  rating?: number | null;
  prizeMode?: 'weight' | 'cooldown';
  cooldownMinutes?: number | null;
  hiddenFromGames?: boolean;
  expectedQuantity?: number | null;
}
export interface StockLike {
  id: number;
  name: string;
  rating?: number | null;
  status?: string;
}
export interface StationLike {
  id: number;
  name: string;
  status?: string;
}
export interface SlotMeta {
  stationId: number;
  stockId: number;
  rating: number;
  prizeMode: 'weight' | 'cooldown';
  cooldownMinutes: number | null;
}
export type NotInGameReason = 'notAllocated' | 'hidden' | 'removed';

type Page = { items?: unknown; total?: unknown } | null | undefined;

/** true sólo si la respuesta trae `items` (array) y `total` (número finito) y total ≤ items.length. */
export function listComplete(page: Page): boolean {
  if (page == null || !Array.isArray(page.items)) return false;
  const total = page.total;
  return typeof total === 'number' && Number.isFinite(total) && total <= page.items.length;
}

/** Estado de las lecturas: 'error' si alguna falló; 'loading' si alguna no llegó; 'tooLong' si alguna no está completa; 'ok'. */
export type DataState = 'ok' | 'loading' | 'error' | 'tooLong';

export function dataState(
  reads: ReadonlyArray<{ status: 'pending' | 'error' | 'success'; data?: { items?: unknown; total?: unknown } | null }>,
): DataState {
  if (reads.some((r) => r.status === 'error')) return 'error';
  if (reads.some((r) => r.status === 'pending')) return 'loading';
  if (reads.some((r) => !listComplete(r.data))) return 'tooLong';
  return 'ok';
}

export type SaveBlock = 'noEvent' | 'eventNotLoaded' | 'loadError' | 'loading' | 'listsIncomplete' | 'pending' | null;

/** Por qué no se puede guardar (null = se puede). El orden es la prioridad. */
export function saveBlock(a: { eventId: number | null; eventLoaded: boolean; data: DataState; result: PlanResult }): SaveBlock {
  if (a.eventId == null) return 'noEvent';
  if (!a.eventLoaded) return 'eventNotLoaded';
  if (a.data === 'error') return 'loadError';
  if (a.data === 'loading') return 'loading';
  if (a.data === 'tooLong') return 'listsIncomplete';
  if (a.result.state === 'PENDING') return 'pending';
  return null;
}

const isEnabled = (status?: string): boolean => status === undefined || status === 'Enable';

/**
 * Precondición: la página sólo llama aquí con `dataState(...) === 'ok'`. Sin datos
 * ciertos no se clasifica nada.
 */
export function poolsFromEvent(
  stations: StationLike[],
  allocations: AllocLike[],
  stocks: StockLike[],
): {
  pools: PlanPool[];
  slots: SlotMeta[];
  emptyStations: StationLike[];
  duplicates: Array<{ stationId: number; stockId: number; rows: number }>;
  notInGame: Array<{ stockId: number; name: string; reason: NotInGameReason }>;
  allocatedStockIds: number[];
  savedExpected: number | null;
} {
  const enabledStations = stations.filter((s) => isEnabled(s.status));
  const stationIds = new Set(enabledStations.map((s) => s.id));
  const stockById = new Map(stocks.map((s) => [s.id, s]));
  const live = allocations.filter((a) => a.status === 'Enable');

  // Jugable = fila Enable, no oculta, en una estación Enable y con merch Enable (o ausente de la lista).
  const playableRows = live.filter((a) => {
    if (a.hiddenFromGames || !stationIds.has(a.stationId)) return false;
    const stock = stockById.get(a.stockId);
    return stock != null && isEnabled(stock.status);
  });
  // Por (estación, merch): la de menor id; el resto, duplicados.
  const byPair = new Map<string, AllocLike[]>();
  for (const a of playableRows) {
    const key = `${a.stationId}:${a.stockId}`;
    const list = byPair.get(key);
    if (list) list.push(a);
    else byPair.set(key, [a]);
  }
  const chosen = new Map<string, AllocLike>();
  const duplicates: Array<{ stationId: number; stockId: number; rows: number }> = [];
  for (const [key, list] of byPair) {
    list.sort((a, b) => a.id - b.id);
    chosen.set(key, list[0]);
    if (list.length > 1) duplicates.push({ stationId: list[0].stationId, stockId: list[0].stockId, rows: list.length });
  }

  const pools: PlanPool[] = [];
  const slots: SlotMeta[] = [];
  const emptyStations: StationLike[] = [];
  for (const st of enabledStations) {
    const poolSlots: PlanSlot[] = [];
    for (const stock of stocks) {
      const row = chosen.get(`${st.id}:${stock.id}`);
      if (!row) continue;
      poolSlots.push({ stockId: stock.id, name: stock.name, weight: slotWeight(row, stock.rating) });
      slots.push({
        stationId: st.id,
        stockId: stock.id,
        rating: row.rating ?? stock.rating ?? 3,
        prizeMode: row.prizeMode === 'cooldown' ? 'cooldown' : 'weight',
        cooldownMinutes: row.cooldownMinutes ?? null,
      });
    }
    if (poolSlots.length > 0) pools.push({ stationId: st.id, stations: 1, slots: poolSlots });
    else emptyStations.push(st);
  }

  const liveByStock = new Map<number, number>();
  for (const a of live) liveByStock.set(a.stockId, (liveByStock.get(a.stockId) ?? 0) + 1);
  const playableStocks = new Set(playableRows.map((a) => a.stockId));
  const notInGame: Array<{ stockId: number; name: string; reason: NotInGameReason }> = [];
  for (const s of stocks) {
    const rows = liveByStock.get(s.id) ?? 0;
    if (!isEnabled(s.status)) {
      if (rows > 0) notInGame.push({ stockId: s.id, name: s.name, reason: 'removed' });
    } else if (rows > 0 && !playableStocks.has(s.id)) {
      notInGame.push({ stockId: s.id, name: s.name, reason: 'hidden' });
    } else if (rows === 0) {
      notInGame.push({ stockId: s.id, name: s.name, reason: 'notAllocated' });
    }
  }

  const allocatedStockIds = [...new Set(live.map((a) => a.stockId))];
  let savedExpected: number | null = null;
  for (const a of live) {
    if (a.expectedQuantity != null) savedExpected = (savedExpected ?? 0) + a.expectedQuantity;
  }
  return { pools, slots, emptyStations, duplicates, notInGame, allocatedStockIds, savedExpected };
}

/** Sin evento: un pool con la merch `Enable` no excluida, con la rareza de la merch. */
export function poolsFromCatalog(
  stocks: StockLike[],
  excluded: ReadonlySet<number>,
  stations: number,
): { pools: PlanPool[] } {
  const slots: PlanSlot[] = stocks
    .filter((s) => isEnabled(s.status) && !excluded.has(s.id))
    .map((s) => ({ stockId: s.id, name: s.name, weight: rarityWeight(s.rating) }));
  return { pools: [{ stationId: null, stations, slots }] };
}

/**
 * Horas que se enseñan con un evento: lo tecleado para ese evento manda; si no, las del
 * evento (1–24 h) sin escribirlas en el borrador; más de 24 h es un evento de varios días
 * y no se adivina.
 */
export function effectiveHours(
  draftHours: string,
  eventHours: number | null,
): { value: string; fromEvent: boolean; multiDay: number | null } {
  if (draftHours.trim() !== '') return { value: draftHours, fromEvent: false, multiDay: null };
  if (eventHours != null && eventHours >= 1 && eventHours <= 24) {
    return { value: String(eventHours), fromEvent: true, multiDay: null };
  }
  if (eventHours != null && eventHours > 24) return { value: '', fromEvent: false, multiDay: eventHours };
  return { value: '', fromEvent: false, multiDay: null };
}
